import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { Button } from '../ui/Button';
import { DataSourceChip } from './DataSourceChip';
import { cn } from '../../lib/utils';

function FilterDropdown({ label, options, value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className={cn('relative min-w-0', disabled && 'opacity-50')} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((current) => !current)}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-lg border px-3 text-left text-[13px] shadow-sm transition',
          disabled
            ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
        )}
      >
        <span className="min-w-0 truncate">{selected?.label || label}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-slate-400', open && 'rotate-180')} />
      </button>
      {open && !disabled ? (
        <div
          className="absolute left-0 top-full z-40 mt-1 max-h-60 min-w-full overflow-y-auto overscroll-contain rounded-lg border border-slate-200 bg-white shadow-lg"
          role="listbox"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={cn(
                'flex w-full px-3 py-2 text-left text-[13px] hover:bg-slate-50',
                option.value === value ? 'font-semibold text-[var(--brand-primary,#1A428A)]' : 'text-slate-700'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function JourneyFilterBar({
  fields,
  values,
  onChange,
  onApply,
  onExport,
  exportLabel = 'Export CSV',
  dataSourceLabel,
  className,
}) {
  return (
    <div className={cn('rounded-xl border border-slate-200 bg-[#F3F5F8] px-3 py-3 sm:px-4', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {fields.map((field) => (
          <div key={field.key} className="min-w-[9.5rem] flex-1 basis-[9.5rem] sm:basis-[11rem]">
            <FilterDropdown
              label={field.label}
              options={field.options}
              value={values[field.key]}
              disabled={field.disabled}
              onChange={(next) => onChange(field.key, next)}
            />
          </div>
        ))}

        {onExport ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onExport}
            className="h-9 shrink-0 gap-2 rounded-lg"
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            {exportLabel}
          </Button>
        ) : null}

        {dataSourceLabel ? (
          <DataSourceChip label={dataSourceLabel} className="h-9 shrink-0" />
        ) : null}

        <Button
          type="button"
          size="sm"
          onClick={onApply}
          className="h-9 shrink-0 !rounded-2xl bg-[var(--brand-primary,#1A428A)] hover:bg-[#163872] sm:min-w-[88px]"
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
